import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { fetchTabletCheckoutPrint, printerLog } from '@/apis/aipass'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import QRCode from 'qrcode.react'
import { PrinterContextCreator } from '@/contexts/printer'
import { errorHandler, toErrorMessage } from '@/libs/errors'
import { CASH_CHANGER } from '@/models/payment-method'
import { TranslateObject } from '@/i18n'
import { SelfCheckOutState } from '@/models/self-checkout'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'
import { checkinEposPrinterIdStorage } from '@/constants/self-checkin-device-storage'
import { TerminalCreditSalesSlip } from '@/components/molecules/front-tablet/receipt/terminal-credit-sales-slip'
import { QrCodeSalesSlip } from '@/components/molecules/front-tablet/receipt/qr-code-sales-slip'
import { reportError } from '@/components/apps/error-boundary'

type Props = {
  roomNumbers: string[]
  hotelId: string
  checkinId: string
  selectedReservationAndRooms: Array<{
    reservationId: string
    guestRoomAssignIds: string[]
  }>
  totalAmount: number
  uploadReservationUuid?: string
  confirm: SelfCheckOutState['confirm']
  payment: SelfCheckOutState['payment']
  onComplete?: () => void
  onFailed?: () => void
}

interface CheckoutPrint {
  guestName: string
  qrCodeUrl: string | null
  hotel: {
    name: TranslateObject
    postalCode: string | undefined
    address: TranslateObject
    receiptRegistrationNumber: string
    receiptLogoImage: string | null
    receiptTaxOfficeName: string | null
  }
}

type QrCodeBlockProps = {
  qrCodeUrl: string
}

const QrCodeBlock: React.FC<QrCodeBlockProps> = ({ qrCodeUrl }) => {
  const { t } = useTranslation()
  return (
    <div className="qr-code-block">
      <p>{t('Thank you for your stay.')}</p>
      <p>{t('Please share your feedback about your stay.')}</p>
      <div>
        <QRCode value={qrCodeUrl} size={200} />
      </div>
      <p>{t('We look forward to welcoming you again.')}</p>
    </div>
  )
}

type HotelBlockProps = {
  hotel: CheckoutPrint['hotel']
}

const HotelBlock: React.FC<HotelBlockProps> = ({ hotel }) => {
  const pickByLanguage = usePickByLanguage()
  return (
    <div className="hotel-block">
      <div style={{ paddingBottom: 8 }}>
        <span>{pickByLanguage(hotel.name)}</span>
      </div>
      {hotel.postalCode && (
        <div>
          <span>〒{hotel.postalCode}</span>
        </div>
      )}
      <div>
        <span>{pickByLanguage(hotel.address)}</span>
      </div>
    </div>
  )
}

export const CheckoutReceipt: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { printImage, disconnect } = useContext(PrinterContextCreator())
  const receiptDom = useRef<HTMLDivElement>(null)
  const [printCheckout, setPrintCheckout] = useState<CheckoutPrint>()

  // 用紙サイズ 80mm=576px
  const paperSize = 576

  const isPaid = props.payment

  const printCheckoutReceipt = async () => {
    try {
      // 選択された客室から、予約IDを取得
      // 客室が選択されていない場合は、その予約IDは選択されていないものとみなす
      const reservationIds = props.selectedReservationAndRooms.filter(r => r.guestRoomAssignIds.length > 0).map(r => r.reservationId)
      setPrintCheckout(
        await fetchTabletCheckoutPrint({ reservationIds: reservationIds, hotelId: props.hotelId, checkinId: props.checkinId }),
      )
    } catch (e) {
      reportError(e)
      alert(t('SelfCheckIn.Failed to print'))
    }
  }

  useEffect(() => {
    if (!receiptDom.current || !printCheckout) {
      return
    }
    html2canvas(receiptDom.current, {
      scale: 1,
      onclone: doc => {
        const div = doc.getElementById('print-content')
        div!.style.display = 'block'
      },
    })
      .then(async receiptImageCanvas => {
        const printReceipt = () =>
          printImage({
            canvas: receiptImageCanvas,
            filename: props.checkinId,
            hotelId: props.hotelId,
            connectIdKey: checkinEposPrinterIdStorage,
            isStayConnected: true,
            reservationUuid: props.uploadReservationUuid,
          })

        try {
          await printReceipt()
          await printerLog({ message: 'success', ...props })
          props.onComplete && props.onComplete()
        } catch (e) {
          await printerLog({ messageRetryConnectionFailed: toErrorMessage(e), ...props })
          for (let retry = 0; retry < 5; retry++) {
            try {
              // 応答しない接続を掴んだまま再試行しても同じ結果になるため、毎回切断して接続し直す
              disconnect()
              await printReceipt()
              props.onComplete && props.onComplete()
              await printerLog({ messageRetry: 'success(retry)', ...props })
              return
            } catch (e) {
              await printerLog({ messageRetryFailed: toErrorMessage(e), ...props })
            }
          }
          await printerLog({ messageRetryAllFailed: toErrorMessage(e), ...props })
          throw e
        }
      })
      .catch(e => {
        reportError(e)
        errorHandler(e)
        props.onFailed && props.onFailed()
      })
  }, [receiptDom, printCheckout])

  useEffect(() => {
    printCheckoutReceipt()
  }, [])

  const receiptProviso = useMemo(() => {
    if (!isPaid) {
      return ''
    }
    let methodName = t('Credit card')
    if (props.payment?.salesSlip?.type === 'qr') {
      methodName = props.payment.paymentMethod
    } else if (props.confirm?.paymentMethod === CASH_CHANGER) {
      methodName = t('Cash')
    }
    return t('SelfCheckIn.Received by {{n}} for accommodation.', { n: methodName })
  }, [props])

  if (!printCheckout) {
    return <></>
  }

  const hasNoAdditionalCharge = props.totalAmount === 0
  const hasQrCodeUrl = printCheckout.qrCodeUrl !== null

  // 追加請求がなく、QRコードもない場合は印刷するものがない
  if (hasNoAdditionalCharge && !hasQrCodeUrl) {
    return <></>
  }

  // 追加請求がなく、QRコードのみある場合はQRコードのみ印刷
  if (hasNoAdditionalCharge && hasQrCodeUrl) {
    return (
      <div id="print-content" ref={receiptDom} css={[{ width: paperSize }, receiptStyle]}>
        <QrCodeBlock qrCodeUrl={printCheckout.qrCodeUrl!} />
        <HotelBlock hotel={printCheckout.hotel} />
      </div>
    )
  }

  return (
    <div id="print-content" ref={receiptDom} css={[{ width: paperSize }, receiptStyle]}>
      {printCheckout.hotel.receiptLogoImage && (
        <div className="logo-block">
          <img src={printCheckout.hotel.receiptLogoImage} alt="receipt logo" />
        </div>
      )}
      {isPaid ? (
        <>
          <div className="payment-card-block">
            <h2>{t('Receipt')}</h2>
            <div className="stats-block">
              <div>
                {t('SelfCheckIn.Issue')} : {dayjs().format('YYYY/MM/DD')}{' '}
              </div>
              <div>
                {t('SelfCheckIn.Reg. no.')} : {printCheckout.hotel.receiptRegistrationNumber}
              </div>
            </div>
            <div className="detail-block">
              <div className="list-item-block">
                <div className="label">{t('address')}</div>
                <div>
                  {props.confirm?.receiptName || printCheckout.guestName}
                  <span style={{ paddingLeft: 16 }}>{t('SelfCheckIn.To')}</span>
                </div>
              </div>
              <hr className="-normal" />
              <div className="list-item-block">
                <div className="label">{t('Amount of money')}</div>
                <div>¥{props.payment!.amount.toLocaleString()}</div>
              </div>
              <hr className="-normal" />
              <div className="breakdown-block">
                <div>
                  {t('SelfCheckIn.For 10%')}¥{props.payment!.totalAmountAt10Per.toLocaleString()}({t('Tax')}¥
                  {props.payment!.totalTaxAt10Per.toLocaleString()})
                </div>
                <div>
                  {t('SelfCheckIn.For 8%')}¥{props.payment!.totalAmountAt8Per.toLocaleString()}({t('Tax')}¥
                  {props.payment!.totalTaxAt8Per.toLocaleString()})
                </div>
              </div>
            </div>

            <div className="note-text">{receiptProviso}</div>
            {printCheckout.hotel.receiptTaxOfficeName && (
              <div className="stamp-tax-declaration-box">
                <p>印紙税申告納</p>
                <p>付につき{printCheckout.hotel.receiptTaxOfficeName}</p>
                <p>税務署承認済</p>
              </div>
            )}
          </div>
          <hr className="-block" />
          {props.payment?.salesSlip?.type === 'credit' && <TerminalCreditSalesSlip receipt={props.payment.salesSlip} />}
          {props.payment?.salesSlip?.type === 'qr' && <QrCodeSalesSlip receipt={props.payment.salesSlip} />}
        </>
      ) : (
        <>
          <div className="payment-front-block">
            <h2>{t('SelfCheckIn.Details of use')}</h2>
            <div className="list-item-block">
              {props.roomNumbers.length > 0 && (
                <div className="list-item">
                  <span className="label">{t('SelfCheckIn.Room')}</span>
                  <span className="value">{props.roomNumbers.join('、')}</span>
                </div>
              )}
              <div className="list-item">
                <span className="label">{t('SelfCheckinPayment.AmountUsed')}</span>
                <span className="value">¥{props.totalAmount.toLocaleString()}</span>
              </div>
              <div className="list-item">
                <span className="label">{t('SelfCheckinPayment.PaymentOption')}</span>
                <span className="value">{t('SelfCheckinPayment.PayAtTheFrontDesk')}</span>
              </div>
            </div>
          </div>
          <hr className="-block" />
        </>
      )}
      {hasQrCodeUrl && (
        <>
          <QrCodeBlock qrCodeUrl={printCheckout.qrCodeUrl!} />
        </>
      )}
      <HotelBlock hotel={printCheckout.hotel} />
    </div>
  )
}

const receiptStyle = css({
  display: 'none',
  fontFamily: 'Noto Sans JP',
  lineHeight: 1.5,
  '.logo-block': {
    textAlign: 'center',
    paddingBottom: 30,
    img: { width: 400, height: 150 },
  },
  '.payment-card-block': {
    padding: '0 64px',
    color: '#272727',
    letterSpacing: 1.2,
    fontSize: 24,
    '.stats-block': { paddingTop: 24 },
    '.detail-block': {
      paddingTop: 48,
      '.list-item-block': {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 28,
        '.label': { width: 100, minWidth: 100 },
      },
    },
    '.breakdown-block': { textAlign: 'right' },
    '.note-text': { paddingTop: 48 },
    '.stamp-tax-declaration-box': {
      width: 220,
      heigth: 160,
      border: '4px solid #272727',
      padding: '0px 4px',
      margin: '32px auto 0',
      fontSize: 22,
      lineHeight: 2.2,
      p: {
        width: '100%',
        whiteSpace: 'nowrap',
        textAlign: 'justify',
        textAlignLast: 'justify',
      },
    },
  },
  '.payment-front-block': {
    padding: '0 64px',
    '.list-item-block': { paddingTop: 48 },
  },
  '.qr-code-block': {
    display: 'flex',
    gap: 20,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '10px',
    p: {
      fontSize: 24,
      color: '#272727',
    },
  },
  '.hotel-block': {
    padding: '24px 64px',
    div: {
      fontSize: 24,
      letterSpacing: 1.2,
      color: '#272727',
    },
  },

  '.list-item': {
    display: 'flex',
    alignItems: 'top',
    color: '#272727',
    lineHeight: 1.2,
    paddingTop: 24,
    '&:first-of-type': { paddingTop: 0 },
    '.label': {
      width: 154,
      minWidth: 154,
      fontSize: 24,
      letterSpacing: 1.2,
    },
    '.value': {
      '::before': { content: '":"', paddingRight: 8 },
      fontSize: 24,
      letterSpacing: 1.2,
      textIndent: '-1em',
      paddingLeft: '1em',
    },
  },

  h2: {
    textAlign: 'center',
    fontSize: 32,
  },
  hr: {
    '&.-normal': { borderTop: '4px solid #272727' },
    '&.-block': {
      borderTop: '4px dashed #A3A3A3',
      margin: '40px 0',
    },
  },
})
